import { useEffect, useState } from "react";

// Empêcher l'écran de la Main de s'éteindre pendant une course.
//
// ## Le problème
// Dès que le téléphone de la Main se verrouille, la page est gelée : plus de
// `watchPosition`, plus de battement de cœur, plus rien. Le client, lui,
// continue de regarder un marqueur qui ne bouge plus.
//
// ## Ce que le web permet, et ce qu'il ne permet pas
// Il faut être net là-dessus, parce que les trois pistes qu'on entend
// habituellement n'ont pas la même valeur :
//
//   • **Service Worker + géolocalisation d'arrière-plan — IMPOSSIBLE.**
//     `navigator.geolocation` n'est PAS exposé dans un `ServiceWorkerGlobalScope`.
//     Ce n'est pas une limite d'implémentation, c'est la spécification. Aucune
//     API web ne rend une position quand la page est gelée. `Periodic Background
//     Sync` ne change rien : Chromium seulement, PWA installée obligatoire,
//     intervalles de l'ordre de l'heure — et toujours aucun accès au GPS.
//
//   • **PWA — utile, mais hors sujet ici.** L'app en est déjà une (manifeste,
//     service worker, icônes). Une PWA mise en arrière-plan est gelée comme
//     n'importe quel onglet. Elle apporte l'installation et les notifications,
//     pas le suivi continu.
//
//   • **Screen Wake Lock — LA seule qui agit vraiment**, et c'est ce module.
//     Elle empêche l'écran de s'éteindre TANT QUE la page est visible. Elle
//     couvre donc le cas de très loin le plus fréquent : la Main pose son
//     téléphone sur le guidon, l'écran s'éteint tout seul au bout d'une minute,
//     et le suivi meurt sans que personne n'ait rien fait.
//
// ## Ce que ce module ne prétend pas faire
// Il ne fait RIEN si la Main bascule sur une autre application ou verrouille
// elle-même son téléphone : le verrou est relâché d'office par le navigateur
// dès que la page cesse d'être visible. C'est une garantie de la plateforme,
// pas un défaut à contourner.
//
// Le suivi réellement continu, écran éteint, exige une application native. Tant
// qu'il n'y en a pas, le reste de la chaîne doit rester honnête : dire au
// client que la position a vieilli plutôt que la laisser figée sous un badge
// « en direct ».

/**
 * - `unsupported` : le navigateur n'a pas l'API (Safari < 16.4, Firefox).
 * - `idle`        : pas demandé, ou relâché parce que la page n'est plus visible.
 * - `active`      : l'écran est maintenu allumé.
 * - `denied`      : la demande a échoué (page non visible, économie de batterie,
 *                   réglage système). On n'insiste pas.
 */
export type WakeLockState = "unsupported" | "idle" | "active" | "denied";

function isSupported(): boolean {
  return typeof navigator !== "undefined" && "wakeLock" in navigator;
}

/**
 * Maintient l'écran allumé tant que `enabled` est vrai.
 *
 * ## Pourquoi il faut le REDEMANDER, et pas seulement le demander
 * Le navigateur relâche le verrou de lui-même dès que la page passe en
 * arrière-plan, et il ne le rend PAS au retour. Sans ré-acquisition sur
 * `visibilitychange`, le verrou est perdu au premier coup d'œil que la Main
 * jette à un message — et l'écran se rendort une minute plus tard, en silence.
 * C'est l'erreur classique avec cette API.
 */
export function useKeepAwake(enabled: boolean): WakeLockState {
  const [state, setState] = useState<WakeLockState>(() =>
    isSupported() ? "idle" : "unsupported",
  );

  useEffect(() => {
    if (!isSupported()) {
      setState("unsupported");
      return;
    }
    if (!enabled) {
      setState("idle");
      return;
    }

    let cancelled = false;
    let sentinel: WakeLockSentinel | null = null;

    const acquire = async () => {
      // Une demande faite page cachée échoue à coup sûr (`NotAllowedError`) :
      // on ne la tente même pas, pour ne pas basculer l'état en `denied` sur
      // un refus qui n'en est pas un.
      if (cancelled || document.visibilityState !== "visible") return;
      if (sentinel && !sentinel.released) return;

      try {
        sentinel = await navigator.wakeLock.request("screen");
        if (cancelled) {
          void sentinel.release();
          sentinel = null;
          return;
        }
        setState("active");
        // Le navigateur peut relâcher de son propre chef (batterie faible).
        // On le note plutôt que de continuer à afficher « actif ».
        sentinel.addEventListener("release", () => {
          if (!cancelled) setState("idle");
        });
      } catch {
        // Refus système : ce n'est pas une panne de l'app, et rien ne sert
        // d'insister. Le suivi continue, simplement l'écran s'éteindra.
        if (!cancelled) setState("denied");
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") void acquire();
    };

    void acquire();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      // Relâcher explicitement : garder l'écran allumé après la fin d'une
      // course viderait la batterie de la Main pour rien.
      if (sentinel && !sentinel.released) void sentinel.release();
      sentinel = null;
    };
  }, [enabled]);

  return state;
}
